import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft,
  Zap,
  GitCompare,
  FolderOpen,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value, suffix = "") => {
  if (value === null || value === undefined || value === "") return "—";
  return `${value}${suffix}`;
};

const NOT_ANALYZED = "Not analyzed";

const getStatusBadge = (status) => {
  const variants = {
    draft:     { style: { backgroundColor: "#B2E6D4", color: "#4a7068" }, label: "Draft" },
    analyzing: { style: { backgroundColor: "#68968915", color: "#689689" }, label: "Analyzing" },
    complete:  { style: { backgroundColor: "#83E8BA20", color: "#83E8BA" }, label: "Complete" },
  };
  const variant = variants[status] || variants.draft;
  return (
    <Badge style={variant.style} variant="secondary">
      {variant.label}
    </Badge>
  );
};

const extractMetrics = (project) => {
  const roi = project.roi_analysis;
  const cost = project.cost_estimate;
  const layout = project.layout;
  const site = project.site_inputs;

  const dimensions =
    site?.land_length && site?.land_width
      ? `${site.land_length}m × ${site.land_width}m`
      : NOT_ANALYZED;

  const locationType = site?.location_type
    ? site.location_type.charAt(0).toUpperCase() + site.location_type.slice(1)
    : NOT_ANALYZED;

  const totalCapex =
    cost?.total_capex?.recommended ?? null;

  const chargerCount =
    layout?.charger_arrangement?.count ?? cost?.chargers?.quantity ?? null;

  const chargerTypes = cost?.chargers?.power_class ?? null;

  const monthlyRevenue = roi?.revenue?.monthly_revenue_inr ?? null;
  const monthlyExpenses = roi?.expenses?.total_monthly_inr ?? null;
  const monthlyNetProfit = roi?.profit?.monthly_net_inr ?? null;
  const paybackYears = roi?.roi?.payback_years ?? null;
  const annualRoi = roi?.roi?.annual_roi_percentage ?? null;

  return {
    dimensions,
    locationType,
    totalCapex,
    chargerCount,
    chargerTypes,
    monthlyRevenue,
    monthlyExpenses,
    monthlyNetProfit,
    paybackYears,
    annualRoi,
    status: project.status,
  };
};

// For numeric rows, find the index of the best value
// "best" depends on the metric:
//   higher is better: monthlyRevenue, monthlyNetProfit, annualRoi, chargerCount
//   lower is better: totalCapex, monthlyExpenses, paybackYears
const getBestIndex = (key, values) => {
  const numeric = values.map((v) => (typeof v === "number" ? v : null));
  if (numeric.every((v) => v === null)) return -1;

  const higherIsBetter = ["monthlyRevenue", "monthlyNetProfit", "annualRoi", "chargerCount"];
  const lowerIsBetter = ["totalCapex", "monthlyExpenses", "paybackYears"];

  if (higherIsBetter.includes(key)) {
    let best = -Infinity;
    let idx = -1;
    numeric.forEach((v, i) => {
      if (v !== null && v > best) { best = v; idx = i; }
    });
    return idx;
  }

  if (lowerIsBetter.includes(key)) {
    let best = Infinity;
    let idx = -1;
    numeric.forEach((v, i) => {
      if (v !== null && v < best) { best = v; idx = i; }
    });
    return idx;
  }

  return -1;
};

const PROJECT_COLORS = ["#689689", "#83E8BA", "#A49E8D"];

const Compare = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API}/projects`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setProjects(data.projects || []);
        } else {
          toast.error("Failed to fetch projects");
        }
      } catch {
        toast.error("Failed to fetch projects");
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const toggleProject = (projectId) => {
    setSelected((prev) => {
      if (prev.includes(projectId)) {
        return prev.filter((id) => id !== projectId);
      }
      if (prev.length >= 3) {
        toast.error("You can compare up to 3 projects at a time");
        return prev;
      }
      return [...prev, projectId];
    });
  };

  const selectedProjects = selected
    .map((id) => projects.find((p) => p.project_id === id))
    .filter(Boolean);

  const metrics = selectedProjects.map((p) => extractMetrics(p));

  const comparisonRows = [
    {
      label: "Site Dimensions",
      key: "dimensions",
      format: (v) => (v === NOT_ANALYZED ? NOT_ANALYZED : v),
      numeric: false,
    },
    {
      label: "Location Type",
      key: "locationType",
      format: (v) => (v === NOT_ANALYZED ? NOT_ANALYZED : v),
      numeric: false,
    },
    {
      label: "Total CAPEX",
      key: "totalCapex",
      format: (v) => (v === null ? NOT_ANALYZED : formatCurrency(v)),
      numeric: true,
    },
    {
      label: "Charger Count",
      key: "chargerCount",
      format: (v) => (v === null ? NOT_ANALYZED : formatNumber(v)),
      numeric: true,
    },
    {
      label: "Charger Types",
      key: "chargerTypes",
      format: (v) => (v === null ? NOT_ANALYZED : v),
      numeric: false,
    },
    {
      label: "Monthly Revenue",
      key: "monthlyRevenue",
      format: (v) => (v === null ? NOT_ANALYZED : formatCurrency(v)),
      numeric: true,
    },
    {
      label: "Monthly Expenses",
      key: "monthlyExpenses",
      format: (v) => (v === null ? NOT_ANALYZED : formatCurrency(v)),
      numeric: true,
    },
    {
      label: "Monthly Net Profit",
      key: "monthlyNetProfit",
      format: (v) => (v === null ? NOT_ANALYZED : formatCurrency(v)),
      numeric: true,
    },
    {
      label: "Payback Period",
      key: "paybackYears",
      format: (v) => (v === null ? NOT_ANALYZED : `${Number(v).toFixed(1)} years`),
      numeric: true,
    },
    {
      label: "Annual ROI %",
      key: "annualRoi",
      format: (v) => (v === null ? NOT_ANALYZED : `${Number(v).toFixed(1)}%`),
      numeric: true,
    },
    {
      label: "Status",
      key: "status",
      format: (v, idx) => getStatusBadge(v),
      numeric: false,
      isStatus: true,
    },
  ];

  const chartData = selectedProjects.length >= 2
    ? [
        {
          name: "Monthly Revenue",
          ...Object.fromEntries(
            selectedProjects.map((p, i) => [
              p.name,
              p.roi_analysis?.revenue?.monthly_revenue_inr ?? 0,
            ])
          ),
        },
        {
          name: "Monthly Expenses",
          ...Object.fromEntries(
            selectedProjects.map((p, i) => [
              p.name,
              p.roi_analysis?.expenses?.total_monthly_inr ?? 0,
            ])
          ),
        },
        {
          name: "Net Profit",
          ...Object.fromEntries(
            selectedProjects.map((p, i) => [
              p.name,
              p.roi_analysis?.profit?.monthly_net_inr ?? 0,
            ])
          ),
        },
      ]
    : [];

  const colCount = selectedProjects.length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f2ec" }} data-testid="compare-page">
      {/* Header */}
      <header className="backdrop-blur sticky top-0 z-40" style={{ backgroundColor: "#504136", borderBottom: "1px solid #5e4e41" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 transition-colors"
              style={{ color: "#B2E6D4" }}
              onMouseEnter={e => e.currentTarget.style.color = "#edf8f4"}
              onMouseLeave={e => e.currentTarget.style.color = "#B2E6D4"}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#689689" }}>
                <Zap className="w-5 h-5" style={{ color: "#faf9f6" }} />
              </div>
              <div className="flex items-center gap-2">
                <h1
                  className="font-bold text-lg"
                  style={{ color: "#edf8f4", fontFamily: "Chivo, sans-serif" }}
                >
                  Compare Projects
                </h1>
                {selectedProjects.length >= 2 && (
                  <Badge style={{ backgroundColor: "#68968915", color: "#689689", border: "1px solid #B2E6D4" }} className="text-xs">
                    {selectedProjects.length} selected
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Project Selection */}
        <Card style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff" }}>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2" style={{ color: "#504136" }}>
              <GitCompare className="w-5 h-5" style={{ color: "#689689" }} />
              Select Projects to Compare
            </CardTitle>
            <p className="text-sm mt-1" style={{ color: "#4a7068" }}>
              Choose 2 or 3 projects to compare side-by-side.
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-16 rounded-lg animate-pulse"
                    style={{ backgroundColor: "#edf8f4" }}
                  />
                ))}
              </div>
            ) : projects.length === 0 ? (
              <div className="py-10 text-center">
                <FolderOpen className="w-10 h-10 mx-auto mb-3" style={{ color: "#4a7068" }} />
                <p className="text-sm" style={{ color: "#4a7068" }}>
                  No projects yet.{" "}
                  <Link
                    to="/new-project"
                    className="hover:underline"
                    style={{ color: "#689689" }}
                  >
                    Create your first project
                  </Link>
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {projects.map((project) => {
                  const isSelected = selected.includes(project.project_id);
                  const isDisabled = !isSelected && selected.length >= 3;
                  return (
                    <label
                      key={project.project_id}
                      className="flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all select-none"
                      style={{
                        borderColor: isSelected ? "#689689" : "#B2E6D4",
                        backgroundColor: isSelected ? "#edf8f4" : isDisabled ? "#edf8f4" : "#ffffff",
                        opacity: isDisabled ? 0.5 : 1,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onCheckedChange={() => {
                          if (!isDisabled) toggleProject(project.project_id);
                        }}
                        className="mt-0.5 shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate" style={{ color: "#504136" }}>
                          {project.name}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: "#4a7068" }}>
                          {project.site_inputs?.land_length}m ×{" "}
                          {project.site_inputs?.land_width}m
                          {project.site_inputs?.location_type
                            ? ` · ${project.site_inputs.location_type}`
                            : ""}
                        </p>
                        <div className="mt-1.5">{getStatusBadge(project.status)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comparison Table */}
        {selectedProjects.length >= 2 && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <Card className="overflow-hidden" style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff" }}>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg" style={{ color: "#504136" }}>Side-by-Side Comparison</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: "1px solid #B2E6D4", backgroundColor: "#edf8f4" }}>
                        <th className="px-6 py-3 text-left font-medium w-44 min-w-[176px]" style={{ color: "#4a7068" }}>
                          Metric
                        </th>
                        {selectedProjects.map((p, i) => (
                          <th
                            key={p.project_id}
                            className="px-6 py-3 text-left font-semibold min-w-[180px]"
                            style={{ color: "#504136" }}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: PROJECT_COLORS[i] }}
                              />
                              <span className="truncate">{p.name}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonRows.map((row, rowIdx) => {
                        const values = metrics.map((m) => m[row.key]);
                        const bestIdx = row.numeric
                          ? getBestIndex(row.key, values)
                          : -1;

                        return (
                          <tr
                            key={row.key}
                            style={{
                              borderBottom: "1px solid #B2E6D4",
                              backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#edf8f4",
                            }}
                          >
                            <td className="px-6 py-4 font-medium whitespace-nowrap" style={{ color: "#4a7068" }}>
                              {row.label}
                            </td>
                            {values.map((val, colIdx) => {
                              const isBest = bestIdx === colIdx;
                              const displayVal = row.isStatus
                                ? getStatusBadge(val)
                                : row.format(val, colIdx);
                              const isNotAnalyzed =
                                displayVal === NOT_ANALYZED ||
                                (typeof displayVal === "string" &&
                                  displayVal === NOT_ANALYZED);

                              return (
                                <td
                                  key={colIdx}
                                  className="px-6 py-4 font-mono"
                                  style={{
                                    color: "#504136",
                                    backgroundColor: isBest && !isNotAnalyzed ? "#83E8BA30" : undefined,
                                  }}
                                >
                                  {row.isStatus ? (
                                    displayVal
                                  ) : isNotAnalyzed ? (
                                    <span className="italic font-sans text-xs" style={{ color: "#4a7068" }}>
                                      Not analyzed
                                    </span>
                                  ) : (
                                    <span
                                      style={isBest ? { fontWeight: 600, color: "#504136" } : {}}
                                    >
                                      {displayVal}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            </motion.div>

            {/* Revenue / Expenses / Profit Bar Chart */}
            {selectedProjects.some((p) => p.roi_analysis) && (
              <Card style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff" }}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg" style={{ color: "#504136" }}>
                    Monthly Financials Comparison
                  </CardTitle>
                  <p className="text-sm" style={{ color: "#4a7068" }}>
                    Revenue, Expenses, and Net Profit across selected projects
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#B2E6D4" />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 12, fill: "#4a7068" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tickFormatter={(v) =>
                            v === 0
                              ? "₹0"
                              : `₹${(v / 100000).toFixed(1)}L`
                          }
                          tick={{ fontSize: 11, fill: "#4a7068" }}
                          axisLine={false}
                          tickLine={false}
                          width={60}
                        />
                        <Tooltip
                          formatter={(value, name) => [
                            formatCurrency(value),
                            name,
                          ]}
                          contentStyle={{
                            borderRadius: "8px",
                            border: "1px solid #B2E6D4",
                            backgroundColor: "#ffffff",
                            color: "#504136",
                            fontSize: "13px",
                          }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: "13px", paddingTop: "12px" }}
                        />
                        {selectedProjects.map((p, i) => (
                          <Bar
                            key={p.project_id}
                            dataKey={p.name}
                            fill={PROJECT_COLORS[i]}
                            radius={[4, 4, 0, 0]}
                            maxBarSize={48}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Empty state when < 2 selected */}
        {selectedProjects.length < 2 && !loading && projects.length >= 2 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#edf8f4" }}>
              <GitCompare className="w-8 h-8" style={{ color: "#4a7068" }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: "#504136" }}>
              Select at least 2 projects
            </h3>
            <p className="text-sm max-w-sm" style={{ color: "#4a7068" }}>
              Check the boxes above to choose which projects you want to compare.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-6" style={{ borderTop: "1px solid #B2E6D4", backgroundColor: "#ffffff" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center justify-end">
          <Link
            to="/contact"
            className="text-sm transition-colors flex items-center gap-1.5"
            style={{ color: "#4a7068" }}
            onMouseEnter={e => e.currentTarget.style.color = "#689689"}
            onMouseLeave={e => e.currentTarget.style.color = "#4a7068"}
          >
            Contact Us
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Compare;
